import type { TrackingPath } from '@cobliteam/api-lib';
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import useMediaStore from '../../../../store/useMediaStore';
import { dateTimeToPercent } from '../MediaTimelineSlider/utils';

interface MapTrackingPathsProps {
  trackingPath: TrackingPath;
  onBasePathChange?: (points: { start: google.maps.LatLng; end: google.maps.LatLng } | null) => void;
  onHighlightPathChange?: (points: { start: google.maps.LatLng; end: google.maps.LatLng } | null) => void;
}

/**
 * Renders and manages vehicle tracking paths on a Google Map
 * Handles both the base path (all points) and highlighted path (selected range)
 */
const MapTrackingPaths = ({ trackingPath, onBasePathChange, onHighlightPathChange }: MapTrackingPathsProps) => {
  // Get the currently selected range from the media store
  const range = useMediaStore((state) => state.range);
  const map = useMap();
  const zoomLevel = useMediaStore((state) => state.zoomLevel);
  const [prevZoomLevel, setPrevZoomLevel] = useState(zoomLevel);

  // References to manage Google Maps objects
  const basePolylineRef = useRef<google.maps.Polyline | null>(null);
  const highlightPolylineRef = useRef<google.maps.Polyline | null>(null);
  const boundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const pointsRef = useRef<google.maps.LatLng[]>([]);

  // Extract data from the tracking path
  const pathCoordinates = trackingPath.geometry.coordinates;
  const timestamps = trackingPath.properties.timestamps;

  // Fit the map to the bounds when the zoom level change is significant
  useEffect(() => {
    if (Math.abs(prevZoomLevel - zoomLevel) > 1 && boundsRef.current) {
      map?.fitBounds(boundsRef.current);
    }

    if (prevZoomLevel !== zoomLevel) {
      setPrevZoomLevel(zoomLevel);
    }
  }, [zoomLevel, prevZoomLevel, map]);

  // Set up the base polyline for the full tracking path
  useEffect(() => {
    // Return early if required data is missing
    if (!(map && pathCoordinates?.length && timestamps)) {
      return;
    }

    // Convert coordinates to Google Maps LatLng points and calculate bounds
    const { points, bounds } = pathCoordinates.reduce(
      (acc, [lng, lat]) => {
        const point = new google.maps.LatLng(lat, lng);
        acc.bounds.extend(point);
        acc.points.push(point);
        return acc;
      },
      {
        points: [] as google.maps.LatLng[],
        bounds: new google.maps.LatLngBounds(),
      }
    );

    // Store references for later use
    boundsRef.current = bounds;
    pointsRef.current = points;

    // Create the base polyline for the full path
    basePolylineRef.current = new google.maps.Polyline({
      path: points,
      geodesic: true,
      strokeColor: '#000000',
      strokeWeight: 5,
      map,
    });

    // Notify parent about start/end points
    onBasePathChange?.({
      start: points[0],
      end: points[points.length - 1],
    });

    // Fit the map to show the entire path
    map.fitBounds(bounds);

    // Clean up on unmount or when dependencies change
    return () => {
      if (basePolylineRef.current) {
        basePolylineRef.current.setMap(null);
        basePolylineRef.current = null;
      }
      onBasePathChange?.(null);
    };
  }, [map, pathCoordinates, timestamps, onBasePathChange]);

  // Handle highlighting the selected range of the path
  useEffect(() => {
    // Return early if required data is missing
    if (!(map && range && pointsRef.current.length && timestamps)) {
      return;
    }

    // Remove any existing highlight polyline
    if (highlightPolylineRef.current) {
      highlightPolylineRef.current.setMap(null);
      highlightPolylineRef.current = null;
    }

    // Filter points that fall within the selected time range
    const highlightedPoints = pointsRef.current.filter((point, index) => {
      const timestamp = timestamps[index];
      const pointDate = new Date(timestamp);
      const pointPercent = dateTimeToPercent(pointDate);
      return pointPercent >= range[0] && pointPercent <= range[1];
    });

    // If we have points to highlight, create a new polyline
    if (highlightedPoints.length > 0) {
      // Calculate bounds for the highlighted section
      const highlightBounds = highlightedPoints.reduce((bounds, point) => bounds.extend(point), new google.maps.LatLngBounds());

      // Create the highlight polyline
      highlightPolylineRef.current = new google.maps.Polyline({
        path: highlightedPoints,
        geodesic: true,
        strokeColor: '#0234E1', // Primary blue
        strokeOpacity: 1,
        strokeWeight: 5,
        map,
        zIndex: 2, // Ensure it renders above the base path
      });

      // Notify parent about start/end points (necessary for the map to zoom to the highlighted section and for the marker to be visible)
      onHighlightPathChange?.({
        start: highlightedPoints[0],
        end: highlightedPoints[highlightedPoints.length - 1],
      });

      // Zoom to fit the highlighted section
      map.fitBounds(highlightBounds);
    }

    // Clean up on unmount or when dependencies change
    return () => {
      if (highlightPolylineRef.current) {
        highlightPolylineRef.current.setMap(null);
        highlightPolylineRef.current = null;
      }
      onHighlightPathChange?.(null);
    };
  }, [map, range, timestamps, onHighlightPathChange]);

  // This component doesn't render any visible elements
  return null;
};

export default MapTrackingPaths;
